/**
 * All API requests in this file are prefixed with WORKFLOWS_BASE_URL.
 * This ensures consistency and makes it easy to update the base path for all related endpoints.
 *
 * Example:
 *   - fetchWorkflows() calls /api/Workflows
 *   - fetchWorkflowLabels() calls /api/Workflows/Labels
 */

import api from './client';
import { Workflow, WorkflowType } from '../containers/WorkflowsPage/types';
import { ILabel } from '../constants/interfaces';

const WORKFLOWS_BASE_URL = '/api/Workflows';

// ── Raw shapes returned by the backend ──────────────────────────────────────

interface ApiWorkflow {
  id: number;
  type: string;
  servicer: string;
  servicerAccountId: string;
  servicerAssetManagerName: string;
  servicerAssetManagerEmail: string;
  caseNumber: string;
  dismissalDate: string;
  assignedDate: string;
  workflowTblId: number;
  linkId: string;
  ndcGroupedDockets: string;
}

interface WorkflowsApiResponse {
  userWorkflows: ApiWorkflow[];
  companyWorkflows: ApiWorkflow[];
}

export interface WorkflowsData {
  myWorkflows: Workflow[];
  companyWorkflows: Workflow[];
  allWorkflows: Workflow[];
}

function mapApiWorkflow(raw: ApiWorkflow): Workflow {
  const dateStr = raw.assignedDate ? raw.assignedDate.split(' ')[0] : '';
  const dismissalDateStr = raw.dismissalDate
    ? raw.dismissalDate.split(' ')[0]
    : '';
  return {
    accountId: raw.servicerAccountId,
    assignedDate: dateStr,
    assignedTo: raw.servicerAssetManagerEmail,
    assignedToName: raw.servicerAssetManagerName.replace(',', ''),
    caseNumber: raw.caseNumber,
    dismissalDate: dismissalDateStr,
    id: raw.id,
    linkId: raw.linkId,
    ndcGroupedDockets: raw.ndcGroupedDockets,
    requestType: raw.type,
    workflowTblId: raw.workflowTblId,
  };
}

export interface LabelResponsePayload {
  labelId: number;
  response: string;
  // childResponses are not used for workflow user responses
}

export async function fetchWorkflows(): Promise<WorkflowsData> {
  const response = await api.get<WorkflowsApiResponse>(WORKFLOWS_BASE_URL);
  const { userWorkflows, companyWorkflows } = response.data;
  const myWorkflows = userWorkflows.map(mapApiWorkflow);
  const mappedCompany = companyWorkflows.map(mapApiWorkflow);
  return {
    allWorkflows: [...myWorkflows, ...mappedCompany],
    companyWorkflows: mappedCompany,
    myWorkflows,
  };
}

export async function fetchWorkflowById(workflowId: string): Promise<Workflow> {
  const response = await api.get(`${WORKFLOWS_BASE_URL}/${workflowId}`);
  return response.data as Workflow;
}

export async function fetchWorkflowLabels(
  workflowType: string
): Promise<ILabel[]> {
  const response = await api.get<ILabel[]>(`${WORKFLOWS_BASE_URL}/Labels`, {
    params: { workflowType },
  });
  return response.data;
}

export async function submitWorkflowResponse(
  workflowId: number,
  userResponses: LabelResponsePayload[]
): Promise<void> {
  await api.put(`${WORKFLOWS_BASE_URL}/${workflowId}`, userResponses);
}

export async function fetchWorkflowTypes(): Promise<WorkflowType[]> {
  const response = await api.get<WorkflowType[]>(`${WORKFLOWS_BASE_URL}/Types`);
  return response.data;
}
